import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Link2, Play } from "lucide-react";
import { VIDEOS } from "../data/videos";

export default function VideoPage() {
  const { slug } = useParams();
  const video = VIDEOS.find((v) => v.slug === slug);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [slug]);

  if (!video) return <Navigate to="/" replace />;

  const copyLink = async () => {
    const url = window.location.href;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        ok = document.execCommand("copy");
      } catch {}
      document.body.removeChild(ta);
    }
    if (ok) toast.success("Link copied!", { description: "Share it with parents, teachers, and fans." });
    else toast.error("Couldn't copy", { description: "Copy the address from your browser bar instead." });
  };

  const others = VIDEOS.filter((v) => v.slug !== slug);

  return (
    <main data-testid="video-page" className="halftone min-h-screen pt-28 pb-20 sm:pt-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link
          data-testid="video-back-btn"
          to="/"
          className="inline-flex items-center gap-2 font-head text-sm font-extrabold uppercase tracking-wide text-ink/60 transition-colors hover:text-maroon"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the series
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 overflow-hidden rounded-3xl border-2 border-ink bg-white shadow-pop-lg"
        >
          <div className="relative border-b-2 border-ink bg-ink">
            <video
              data-testid="video-page-player"
              controls
              autoPlay
              playsInline
              poster={video.poster}
              className="aspect-video w-full object-cover"
            >
              <source src={video.src} type="video/mp4" />
            </video>
            <span
              className="sticker pointer-events-none absolute left-4 top-4 flex -rotate-2 items-center gap-1.5 rounded-full text-[10px]"
              style={{ backgroundColor: video.color, color: "#FFFDF6" }}
            >
              <Play className="h-3 w-3 fill-current" /> Now playing
            </span>
          </div>
          <div className="flex flex-col items-start justify-between gap-5 p-6 sm:flex-row sm:items-center sm:p-8">
            <div>
              <h1 className="font-head text-2xl font-black tracking-tight sm:text-3xl">{video.name}</h1>
              <p className="mt-2 max-w-lg text-base leading-relaxed text-ink/70">{video.desc}</p>
            </div>
            <button
              data-testid="video-copy-link-btn"
              onClick={copyLink}
              className="flex shrink-0 items-center gap-2 rounded-full border-2 border-ink bg-banana px-6 py-3 font-head text-sm font-extrabold uppercase tracking-wide shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm"
            >
              <Link2 className="h-4 w-4" /> Copy link
            </button>
          </div>
        </motion.div>

        <h2 className="mt-14 font-mono text-xs font-bold uppercase tracking-[0.3em] text-ink/50">More to watch</h2>
        <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {others.map((v) => (
            <Link
              key={v.slug}
              data-testid={`video-related-${v.slug}`}
              to={`/videos/${v.slug}`}
              className="group overflow-hidden rounded-2xl border-2 border-ink bg-white shadow-pop transition-all duration-300 hover:-translate-y-1.5 hover:shadow-pop-lg"
            >
              <div className="relative border-b-2 border-ink">
                <img src={v.poster} alt={`${v.name} thumbnail`} className="aspect-video w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors duration-300 group-hover:bg-ink/25">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-ink bg-banana opacity-0 shadow-pop transition-opacity duration-300 group-hover:opacity-100">
                    <Play className="h-5 w-5 fill-current" />
                  </span>
                </span>
              </div>
              <p className="p-4 font-head text-base font-extrabold tracking-tight">{v.name}</p>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
